// ============================================================
// MOVEMENT BANDS - where one band ends and the next begins.
//
// The bands used to be four tinted fills laid over the ground. Any
// translucent fill takes the hue of what is under it, so a band drawn
// over amber rough going came out orange, over dark impassable ground it
// vanished, and the map read as though the paint meant something about
// movement. It did not: the fill was the same everywhere and the GROUND
// was doing the talking.
//
// So the bands are drawn as EDGES instead - the boundary between one
// band and the next, and between the reachable area and everything past
// it. A line is opaque, so it says the same thing over any colour, and
// the painted ground shows through unaltered because nothing is laid on
// top of it.
//
// Pure. Zero React dependency.
// ============================================================
import { tileKey } from './movement';
import type { ReachableTile } from './movement';

export interface MovementBand {
  /** The cost at which this band ends. */
  limit: number;
  /** half | full | charge | run - picks the colour. */
  key: string;
}

export interface BandEdge {
  /** Which band this edge belongs to - the INNER side of the boundary. */
  key: string;
  /** The storey it is drawn on, in metres. */
  y: number;
  x1: number;
  z1: number;
  x2: number;
  z2: number;
}

/** Which band this tile falls in, by the cost of reaching it. */
export function bandFor(cost: number, bands: MovementBand[]): string | null {
  for (const band of bands) if (cost <= band.limit + 1e-9) return band.key;
  return null;
}

/** The four neighbours of a tile, in world metres. */
const STEPS = [
  { dx: 1, dz: 0 },
  { dx: -1, dz: 0 },
  { dx: 0, dz: 1 },
  { dx: 0, dz: -1 },
];

/**
 * The boundary segments of every band.
 *
 * A tile contributes an edge on any side where its neighbour is in a
 * DIFFERENT band, or where there is no neighbour at all - which is what
 * draws the outline of the reachable area, and the outline of every hole
 * in it. A hole is a wall or impassable ground, and having it outlined is
 * the point: unreachable ground that merely goes uncoloured reads as open
 * ground nobody has walked to yet.
 *
 * Coordinates are the tile's own world metres; `size` is the tile.
 */
export function bandEdges(tiles: ReachableTile[], bands: MovementBand[] | null | undefined, size = 1): BandEdge[] {
  if (!tiles.length) return [];
  const s = size > 0 ? size : 1;
  const byKey = new Map<string, ReachableTile>();
  for (const tile of tiles) byKey.set(tileKey(tile.x, tile.z, s), tile);
  const keyOf = (tile: ReachableTile) => bandKey(tile, bands);

  return tiles.flatMap((tile) => {
    const mine = keyOf(tile);
    return STEPS.filter((step) => {
      const neighbour = byKey.get(tileKey(tile.x + step.dx * s, tile.z + step.dz * s, s));
      return !(neighbour && keyOf(neighbour) === mine);
    }).map((step) => edgeSegment(tile, step, s / 2, mine));
  });
}

/** Which band a tile is in. With no bands every tile is one band, so only the outline survives. */
function bandKey(tile: ReachableTile, bands: MovementBand[] | null | undefined): string {
  return (bands?.length ? bandFor(tile.cost, bands) : 'reach') ?? 'reach';
}

/**
 * The side a tile shares with its neighbour one step away, as a segment
 * perpendicular to the step.
 *
 * THE STOREY THE EDGE IS ON, so a contour round a rooftop is drawn on the
 * roof instead of on the floor below it (v7.97.0).
 */
function edgeSegment(tile: ReachableTile, step: { dx: number; dz: number }, h: number, key: string): BandEdge {
  const cx = tile.x + step.dx * h;
  const cz = tile.z + step.dz * h;
  const y = tile.y ?? 0;
  return step.dx !== 0
    ? { key, y, x1: cx, z1: cz - h, x2: cx, z2: cz + h }
    : { key, y, x1: cx - h, z1: cz, x2: cx + h, z2: cz };
}

/** How loud the reach is drawn (engine/battlefield/movementBandLook): quiet hairlines, full, or off. */
export type ReachLook = 'off' | 'quiet' | 'full';

/**
 * What a look draws of a reach: every tile and its band edges - or, Off, the dangerous tiles alone (a hazard is never
 * hidden by a look). Shared by the svg overlay and the GPU map.
 */
export function reachDrawn(
  look: ReachLook,
  tiles: ReachableTile[],
  bands: MovementBand[] | null | undefined,
  size: number,
) {
  if (look === 'off') return { shown: tiles.filter((t) => t.terrain === 'dangerous'), edges: [] as BandEdge[] };
  return { shown: tiles, edges: bandEdges(tiles, bands, size) };
}

/** How long the take-cover cue's ripple takes to cross the reach, in ms (the user's pick "B", board 8). */
const RIPPLE_MS = 700;

/** A graded tile's ripple delay: by its path cost against the cost the grading stops at. Shared by both renderers. */
export const rippleDelay = (cost: number, max: number) =>
  max > 0 ? Math.round(Math.min(1, cost / max) * RIPPLE_MS) : 0;
